import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import {
  saveProfileDraft,
  loadProfileDraft,
  clearProfileDraft,
  markProfileFreshStart,
  clearProfileFreshFlag,
  isProfileFreshStart,
} from '../utils/profileFormPersistence';

interface EditProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpdate: () => void;
}

const EditProfileModal: React.FC<EditProfileModalProps> = ({ isOpen, onClose, onUpdate }) => {
  const { user, updateUser } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    city: '',
    state: '',
    address: '',
    pincode: '',
    gstNumber: '',
    panNumber: '',
  });
  const [profileImage, setProfileImage] = useState<File | null>(null);
  const [profileImagePreview, setProfileImagePreview] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{[key: string]: string}>({});
  const [showRestore, setShowRestore] = useState(false);
  const [draftData, setDraftData] = useState<any>(null);

  // Initialize form data when modal opens
  useEffect(() => {
    if (isOpen && user) {
      // Check for draft first
      if (!isProfileFreshStart()) {
        const draft = loadProfileDraft();
        if (draft) {
          setDraftData(draft);
          setShowRestore(true);
          return; // Don't load user data if we have a draft
        }
      }
      
      setFormData({
        name: user.name || '',
        phone: user.phone || '',
        city: user.city || '',
        state: user.state || '',
        address: user.address || '',
        pincode: user.zip || '',
        gstNumber: user.gstNumber || '',
        panNumber: user.panNumber || '',
      });
      setProfileImage(null);
      setProfileImagePreview(user.profileImageUrl || '');
      setError('');
      setSuccess(false);
      setValidationErrors({});
    }
  }, [isOpen, user]);

  // Save draft on form changes
  useEffect(() => {
    if (!isOpen || showRestore || !user) return;
    
    // Debounce save to avoid too many writes
    const timeoutId = setTimeout(() => {
      saveProfileDraft(formData);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [formData, isOpen, showRestore, user]);

  const validateForm = (): boolean => {
    const errors: {[key: string]: string} = {};

    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }

    if (!formData.phone.trim()) {
      errors.phone = 'Phone number is required';
    } else if (!/^\d{10}$/.test(formData.phone)) {
      errors.phone = 'Enter a valid 10-digit mobile number';
    }

    if (!formData.address.trim()) {
      errors.address = 'Address is required';
    }

    if (!formData.city.trim()) {
      errors.city = 'City is required';
    }

    if (!formData.state.trim()) {
      errors.state = 'State is required';
    }

    if (!formData.pincode.trim()) {
      errors.pincode = 'Pincode is required';
    } else if (!/^\d{6}$/.test(formData.pincode)) {
      errors.pincode = 'Pincode must be 6 digits';
    }

    if (formData.gstNumber && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(formData.gstNumber)) {
      errors.gstNumber = 'Please enter a valid GST number';
    }

    if (formData.panNumber && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(formData.panNumber)) {
      errors.panNumber = 'Please enter a valid PAN number';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    let nextValue = value;
    if (name === 'phone') {
      // Only digits, 10 max
      nextValue = value.replace(/\D/g, '').slice(0, 10);
    } else if (name === 'gstNumber' || name === 'panNumber') {
      // Uppercase and trim spaces
      nextValue = value.toUpperCase().replace(/\s/g, '');
    }
    setFormData(prev => ({ ...prev, [name]: nextValue }));

    // Clear validation error when user starts typing
    let fieldError = '';
    if (name === 'phone' && nextValue) {
      fieldError = /^\d{10}$/.test(nextValue) ? '' : 'Enter a valid 10-digit mobile number';
    } else if (name === 'gstNumber' && nextValue) {
      fieldError = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(nextValue) ? '' : 'Please enter a valid GST number';
    } else if (name === 'panNumber' && nextValue) {
      fieldError = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(nextValue) ? '' : 'Please enter a valid PAN number';
    }
    setValidationErrors(prev => ({ ...prev, [name]: fieldError }));
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select a valid image file');
        return;
      }
      
      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size should be less than 5MB');
        return;
      }

      setProfileImage(file);
      
      // Create preview URL
      const reader = new FileReader();
      reader.onload = (e) => {
        setProfileImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      console.log('Sending profile update request:', formData);
      const response = await apiService.updateProfile(formData, profileImage);
      console.log('Profile update response:', response);
      
      if (response.success) {
        console.log('Profile update response data:', response.data);
        console.log('Profile image URL from response:', (response.data as any)?.data?.profileImageUrl);
        
        // Update the user data in context with the new profile data
        const updatedUserData = {
          name: formData.name,
          phone: formData.phone,
          city: formData.city,
          state: formData.state,
          address: formData.address,
          zip: formData.pincode,
          gstNumber: formData.gstNumber,
          panNumber: formData.panNumber,
          profileImageUrl: (response.data as any)?.data?.profileImageUrl || user?.profileImageUrl,
        };
        
        console.log('Updating user with data:', updatedUserData);
        updateUser(updatedUserData);
        
        clearProfileDraft();
        clearProfileFreshFlag();
        setDraftData(null);
        setShowRestore(false);
        setSuccess(true);
        setTimeout(() => {
          onUpdate();
          onClose();
        }, 1000); // Show success message for 1 second
      } else {
        setError(response.error || 'Failed to update profile');
      }
    } catch (error) {
      console.error('Profile update error:', error);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRestore = () => {
    if (draftData) {
      setFormData(prev => ({
        ...prev,
        ...draftData,
        // Profile image cannot be restored
      }));
    }
    clearProfileFreshFlag();
    setShowRestore(false);
    setDraftData(null);
  };

  const handleStartFresh = () => {
    clearProfileDraft();
    markProfileFreshStart();
    setShowRestore(false);
    setDraftData(null);
    // Reload user data (reset to original user data)
    if (user) {
      setFormData({
        name: user.name || '',
        phone: user.phone || '',
        city: user.city || '',
        state: user.state || '',
        address: user.address || '',
        pincode: user.zip || '',
        gstNumber: user.gstNumber || '',
        panNumber: user.panNumber || '',
      });
      setProfileImage(null);
      setProfileImagePreview(user.profileImageUrl || '');
      setValidationErrors({});
      setError('');
      setSuccess(false);
    }
  };

  const handleClose = () => {
    clearProfileFreshFlag();
    setShowRestore(false);
    setDraftData(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <React.Fragment>
      {showRestore && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full p-6">
            <h2 className="text-lg font-semibold mb-2">Restore Form?</h2>
            <p className="text-sm text-gray-600 mb-4">
              Unsaved form data was found. Do you want to restore it?
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={handleStartFresh}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
              >
                Start Fresh
              </button>
              <button
                onClick={handleRestore}
                className="px-4 py-2 bg-jd-blue text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Restore
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Edit Profile</h2>
          <button
            onClick={() => {
              if (showRestore) return;
              handleClose();
            }}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={showRestore}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          {/* Success Message */}
          {success && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-6">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium">Profile updated successfully!</p>
                </div>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Profile Image Upload */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Profile Image
            </label>
            <div className="flex items-center space-x-4">
              <div className="relative">
                {profileImagePreview ? (
                  <img 
                    src={profileImagePreview} 
                    alt="Profile Preview" 
                    className="w-20 h-20 rounded-full object-cover border-2 border-gray-200"
                  />
                ) : (
                  <div className="w-20 h-20 bg-gradient-to-r from-jd-blue to-jd-green rounded-full flex items-center justify-center">
                    <span className="text-white font-bold text-lg">
                      {formData.name ? formData.name.charAt(0).toUpperCase() : 'V'}
                    </span>
                  </div>
                )}
              </div>
              <div className="flex-1">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-jd-blue file:text-white hover:file:bg-blue-700 file:cursor-pointer cursor-pointer"
                />
                <p className="text-xs text-gray-500 mt-1">
                  JPEG, JPG and PNG. Max size 5MB.
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left Column */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Vendor Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-jd-blue focus:border-transparent ${
                    validationErrors.name ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Enter vendor name"
                />
                {validationErrors.name && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contact Number *
                </label>
                <input
                  type="text"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-jd-blue focus:border-transparent ${
                    validationErrors.phone ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Enter contact number"
                />
                {validationErrors.phone && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.phone}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Address *
                </label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-jd-blue focus:border-transparent ${
                    validationErrors.address ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Enter address"
                />
                {validationErrors.address && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.address}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  City *
                </label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-jd-blue focus:border-transparent ${
                    validationErrors.city ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Enter city"
                />
                {validationErrors.city && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.city}</p>
                )}
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  State *
                </label>
                <input
                  type="text"
                  name="state"
                  value={formData.state}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-jd-blue focus:border-transparent ${
                    validationErrors.state ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Enter state"
                />
                {validationErrors.state && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.state}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Pincode *
                </label>
                <input
                  type="text"
                  name="pincode"
                  value={formData.pincode}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-jd-blue focus:border-transparent ${
                    validationErrors.pincode ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Enter pincode"
                  maxLength={6}
                />
                {validationErrors.pincode && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.pincode}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  GST Number
                </label>
                <input
                  type="text"
                  name="gstNumber"
                  value={formData.gstNumber}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-jd-blue focus:border-transparent ${
                    validationErrors.gstNumber ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Enter GST number (optional)"
                />
                {validationErrors.gstNumber && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.gstNumber}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  PAN Number
                </label>
                <input
                  type="text"
                  name="panNumber"
                  value={formData.panNumber}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-jd-blue focus:border-transparent ${
                    validationErrors.panNumber ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Enter PAN number (optional)"
                />
                {validationErrors.panNumber && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.panNumber}</p>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end space-x-3 mt-8 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={handleClose}
              disabled={showRestore}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || showRestore}
              className="px-4 py-2 bg-jd-blue text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-jd-blue disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? (
                <div className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Updating...
                </div>
              ) : (
                'Update Profile'
              )}
            </button>
          </div>
        </form>
      </div>
      </div>
    </React.Fragment>
  );
};

export default EditProfileModal;
